/**
 * Urlaubsantrag stellen (POST /leave-requests, AP 3.2).
 *
 * Self-Service: ohne Mitarbeiter-Auswahl gilt der mit dem Login verknuepfte
 * Mitarbeiter (Backend loest ueber employees.logto_user_id auf);
 * personal_schreiben darf Antraege fuer alle stellen. Nach dem Anlegen wird
 * der Konto-Saldo als Vorschau-Toast angezeigt; 409-Konflikte erscheinen als
 * Liste im Dialog, 422 (z. B. zu wenig Restsaldo) als Fehlermeldung.
 */

import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { ABSENCE_REASONS } from "@/lib/absenceColors"
import { fmtDate, fmtDays } from "@/lib/format"
import { useCan } from "@/lib/permissions"
import { api, apiConflicts, apiError } from "@/services/api"
import { BusinessDayRangePicker } from "@/components/shared"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import type { AbsenceConflict, LeaveRequest } from "@/types"

interface VacationRequestDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Fest vorgegebener Mitarbeiter (Personalakte-Tab); sonst Select */
  fixedEmployeeId?: string
  /** Vorbelegung des Zeitraums */
  defaultFrom?: string
  defaultTo?: string
}

export function VacationRequestDialog({
  open,
  onOpenChange,
  fixedEmployeeId,
  defaultFrom,
  defaultTo,
}: VacationRequestDialogProps) {
  const queryClient = useQueryClient()
  const canWrite = useCan("personal_schreiben")

  const [employeeId, setEmployeeId] = useState(fixedEmployeeId ?? "")
  const [from, setFrom] = useState(defaultFrom ?? "")
  const [to, setTo] = useState(defaultTo ?? "")
  const [halfDayStart, setHalfDayStart] = useState(false)
  const [halfDayEnd, setHalfDayEnd] = useState(false)
  const [substituteId, setSubstituteId] = useState("")
  const [note, setNote] = useState("")
  const [conflicts, setConflicts] = useState<AbsenceConflict[] | null>(null)

  // Bei jedem Oeffnen zuruecksetzen
  useEffect(() => {
    if (open) {
      setEmployeeId(fixedEmployeeId ?? "")
      setFrom(defaultFrom ?? "")
      setTo(defaultTo ?? "")
      setHalfDayStart(false)
      setHalfDayEnd(false)
      setSubstituteId("")
      setNote("")
      setConflicts(null)
    }
    // Vorbelegungen sind beim Oeffnen fix
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const employees = useQuery({
    queryKey: ["employees", { size: 100 }],
    queryFn: () => api.employees.list({ size: 100 }),
    enabled: open,
  })
  const employeeOptions = employees.data?.data ?? []

  const create = useMutation({
    mutationFn: () =>
      api.leave.requests.create({
        employeeId: employeeId ? Number(employeeId) : null,
        startDate: from,
        endDate: to,
        halfDayStart,
        halfDayEnd,
        note: note.trim() || null,
        substituteEmployeeId: substituteId ? Number(substituteId) : null,
      }),
    onSuccess: async (request: LeaveRequest) => {
      queryClient.invalidateQueries({ queryKey: ["leave"] })
      queryClient.invalidateQueries({ queryKey: ["absences"] })
      toast.success(`Urlaubsantrag gestellt: ${fmtDays(request.requestedDays)} Betriebstage`)
      onOpenChange(false)
      // Balance-Vorschau nach dem Anlegen (Konto-Jahr = Jahr des Anfangsdatums)
      try {
        const year = Number(request.startDate.slice(0, 4))
        const bal = await api.leave.account.get(request.employeeId, year)
        toast.info(
          `Urlaubskonto ${year}: ${fmtDays(bal.remainingDays)} Tage verbleibend ` +
            `(${fmtDays(bal.pendingDays)} beantragt, ${fmtDays(bal.takenDays)} genommen)`
        )
      } catch {
        // Vorschau ist Komfort — Fehler hier nicht eskalieren
      }
    },
    onError: (e: unknown) => {
      const list = apiConflicts(e)
      if (list) setConflicts(list)
      else toast.error(apiError(e))
    },
  })

  const valid = from !== "" && to !== "" && from <= to

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Urlaub beantragen</DialogTitle>
          <DialogDescription>
            Gezaehlt werden Betriebstage; der Antrag startet im Status „Beantragt“.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          {!fixedEmployeeId && (
            <div className="space-y-1.5">
              <Label>Mitarbeiter</Label>
              <Select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
              >
                <option value="">Ich selbst (verknüpfter Mitarbeiter)</option>
                {canWrite &&
                  employeeOptions.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.displayName}
                    </option>
                  ))}
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Von *</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Bis *</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>

          <BusinessDayRangePicker
            from={from}
            to={to}
            onChange={(f, t) => {
              setFrom(f)
              setTo(t)
            }}
            halfDayStart={halfDayStart}
            halfDayEnd={halfDayEnd}
          />

          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <Switch checked={halfDayStart} onCheckedChange={setHalfDayStart} />
              <Label className="font-normal">Halber Tag (Anfang)</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={halfDayEnd} onCheckedChange={setHalfDayEnd} />
              <Label className="font-normal">Halber Tag (Ende)</Label>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Vertretung (optional)</Label>
            <Select value={substituteId} onChange={(e) => setSubstituteId(e.target.value)}>
              <option value="">— keine Vertretung —</option>
              {employeeOptions
                .filter((emp) => emp.id !== (employeeId || fixedEmployeeId))
                .map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.displayName}
                  </option>
                ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Notiz (optional)</Label>
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="z. B. Grund oder Hinweise für die Genehmigung"
            />
          </div>

          {conflicts && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
              <p className="mb-2 text-sm font-medium text-destructive">
                Der Zeitraum überlappt bestehende Abwesenheiten:
              </p>
              <ul className="space-y-1">
                {conflicts.map((c) => (
                  <li key={c.id} className="flex items-center gap-2 text-sm">
                    <Badge variant="outline" className={ABSENCE_REASONS[c.reason].badge}>
                      {ABSENCE_REASONS[c.reason].label}
                    </Badge>
                    <span>
                      {fmtDate(c.from)} – {fmtDate(c.to)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            disabled={!valid || create.isPending}
            onClick={() => {
              setConflicts(null)
              create.mutate()
            }}
          >
            Antrag stellen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
